import { useEffect, useState, useMemo } from "react";
import { Formik, Form } from "formik";
import { useParams } from "react-router-dom";

import { useDispatch } from "react-redux";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import Header from "layouts/pages/settings/components/Header";
import RecentFlows from "layouts/pages/account/company/components/Overview/components/RecentFlows";


import GoBackButton from "./components/GoBackButton";

import { useAuth0 } from "@auth0/auth0-react";

import { getQuery, updateQuery } from "api/UdasWrapper";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ProtectedButton from "authentication/ProtectedButton";
import { addInfoLog, addErrorLog } from "../../../Logger/logger";
import { useRoles } from "hooks/useRoles";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useUserFlows } from "hooks/useUserFlows";
import { useUsers } from "hooks/useUsers";
import { PERMISSIONS } from "constants/portal";
import { UPDATE_USER } from "redux/constants/portal";

import ProfileInfo from "layouts/pages/home/components/Settings/components/ProfileInfo";
import ProfileDetailsRows from "./components/ProfileDetailsRows";
import validations from "layouts/pages/home/components/Settings/schemas/validations";
import profileForm from "layouts/pages/home/components/Settings/schemas/form";

function SectionCard({ title, children, action }) {
  return (
    <Card
      sx={{
        height: "auto",
        boxShadow: "none !important",
        borderRadius: "8px !important",
        background: "var(--portal-surface-bg) !important",
        border: "1px solid var(--portal-surface-border) !important",
      }}
    >
      <SoftBox p={{ xs: 2.5, sm: 3 }} display="flex" alignItems="center" justifyContent="space-between">
        <SoftTypography variant="h5" fontWeight="bold" className="white-to-dark">
          {title}
        </SoftTypography>

        {action ? <SoftBox>{action}</SoftBox> : null}
      </SoftBox>

      <SoftBox px={{ xs: 2.5, sm: 3 }} pb={{ xs: 2.5, sm: 3 }}>{children}</SoftBox>
    </Card>
  );
}

function Settings({ forView }) {
	const [roleOptions, setRoleOptions] = useState(null);
	const [isOwner, setIsOwner] = useState(false);
	const [ownerId, setOwnerId] = useState(null);
	const [, setDisableRoleField] = useState(false);
	const [otherUser, setOtherUser] = useState(null);
	const { id } = useParams();
	const { user } = useAuth0();
	const { roles } = useRoles(false);
	const { flows } = usePortalFlows();
	const { userFlows } = useUserFlows();
	const [recentFlows, setRecentFlows] = useState([]);
	const { users } = useUsers(false);
	const dispatch = useDispatch();
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const userRoleName = useMemo(() => {
		if (roles.length && otherUser) {
			return roles.find(({ id }) => id === otherUser.role_id)?.name;
		}
	}, [otherUser, roles]);

	const getOtherUser = async (userAuth0Id) => {
		if (userAuth0Id) {
			let data = JSON.stringify({
				query: `query Userbyid($auth0_id: String!, $tenant_id: String!) {
          userbyid(auth0_id: $auth0_id, tenant_id: $tenant_id) {
            auth0_id
            tenant_id
            username
            user_type
            role_id
            status
            first_name
            last_name
            organization
            mobile
            phone
            email_address
            created_date_time
            language
            your_location
            image
          }
        }`,
				variables: { auth0_id: userAuth0Id, tenant_id: `${user.stripeId}` }
			});
			try {
				const response = await getQuery(data);
				const user = response?.userbyid;
				addInfoLog(
					"response from getQuery of settings setOtherUser ==",
					response?.userbyid
				);
				setOtherUser({
					id: user?.id || "",
					auth0_id: user?.auth0_id || "",
					tenant_id: user?.tenant_id || "",
					username: user?.username || "",
					user_type: user?.user_type || "",
					role_id: user?.role_id || "",
					status: user?.status || "",
					first_name: user?.first_name || "",
					last_name: user?.last_name === " " ? "" : user?.last_name,
					organization:
						user?.organization === " " ? "" : user?.organization,
					mobile: user?.mobile === " " ? "" : user?.mobile,
					phone: user?.phone === " " ? "" : user?.phone,
					email_address: user?.email_address || "",
					your_location:
						user?.your_location === " " ? "" : user?.your_location,
					language: user?.language === " " ? "English" : user?.language,
					image: user?.image || "",
					created_date_time: user?.created_date_time || ""
				});
			} catch (error) {
				console.log(error);
				addErrorLog("query Userbyid", error, user?.tenant_id);
			}
		}
	};

	const determineIfOwner = () => {
		try {
			if (users && roles.length) {
				const ownerRoleId = roles.find(
					(role) => role.name.toLowerCase() === "owner"
				).id;
				const userRoleId = users.find(
					(item) => item.auth0_id === user.cxfUserId
				)?.role_id;
				userRoleId === ownerRoleId && setIsOwner(true);
				setOwnerId(
					users.find((item) => item.role_id === ownerRoleId).auth0_id
				);
			}
		} catch (err) {
			console.log(err);
		}
	};

	useEffect(() => {
		if (id) {
			getOtherUser(id);
		}
	}, [id]);

	useEffect(() => {
		determineIfOwner();
	}, [users, roles]);

	useEffect(() => {
		if (roles.length && ownerId) {
			if (ownerId === id) {
				setRoleOptions([
					...roles.map((role) => ({ value: role.id, label: role.name }))
				]);
			} else {
				setRoleOptions([
					...roles
						.filter((role) => {
							if (isOwner === false) {
								return role.name.toLowerCase() !== "owner";
							} else {
								return role;
							}
						})
						.map((role) => ({ value: role.id, label: role.name }))
				]);
			}
		}
	}, [isOwner, roles, ownerId]);

	useEffect(() => {
		if (ownerId) {
			if (ownerId === id && ownerId !== user?.cxfUserId) {
				setDisableRoleField(true);
			}
		}
	}, [ownerId]);

	const handleSubmit = async (values) => {
		const input = {
			email_address: values.email_address || "",
			first_name: values.first_name || "",
			last_name: values.last_name || "",
			organization: values.organization || "",
			mobile: values.mobile || "",
			phone: values.phone || "",
			role_id: values.role_id || "",
			user_type: values.user_type || "tenant",
			username: values.email_address || "",
			image: otherUser.image || "",
			your_location: values.your_location || "",
			language: values.language || "English",
			status: values.status || "",
		};
		setIsSaving(true);
		try {
			const response = await updateQuery(JSON.stringify({
				query: `mutation UpdateUser($tenantId: String, $auth0Id: String, $input: UserUpdateInput) {
					updateUser(tenant_id: $tenantId, auth0_id: $auth0Id, input: $input) {
						isSuccessful message resultObject
					}
				}`,
				variables: { tenantId: user.stripeId, auth0Id: otherUser.auth0_id, input },
			}));
			if (response?.updateUser?.isSuccessful === false) throw new Error(response.updateUser.message);
			setOtherUser((current) => ({ ...current, ...input }));
			dispatch({ type: UPDATE_USER, payload: { ...input, auth0_id: otherUser.auth0_id, tenant_id: otherUser.tenant_id } });
			toast.success("User profile updated");
			setIsEditing(false);
		} catch (error) {
			toast.error(error?.message || "Failed to update user profile");
		} finally {
			setIsSaving(false);
		}
	};

	const isDataLoaded = otherUser && roles.length && roleOptions;

	useEffect(() => {
		if (!flows.length || !user) return;

		const currentUserFlows = flows.filter(
			(flow) => flow.created_by_id === user.cxfUserId
		);

		if (user.cxfUserId === id) {
			setRecentFlows(currentUserFlows.slice(0, 3));

			return;
		}

		const currentUserSharedFlows = currentUserFlows
			?.filter((flow) => {
				return userFlows.find(
					(userFlow) =>
						userFlow.flow_id === flow.id && userFlow.user_id === id
				);
			})
			?.sort((a, b) => {
				const aDate = a.updated_date_time
					? new Date(a.updated_date_time)
					: new Date(a.created_date_time);
				const bDate = b.updated_date_time
					? new Date(b.updated_date_time)
					: new Date(b.created_date_time);
				return bDate - aDate;
			});

		setRecentFlows(currentUserSharedFlows.slice(0, 3));
	}, [flows, userFlows, user, id]);

	if (forView && isDataLoaded) {
		return (
			<>
				<SoftBox sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
					<DashboardNavbar title="Profile Overview" flush />
				</SoftBox>

				<DashboardLayout flush>
					<SoftBox
						sx={{ width: "100%", maxWidth: "1600px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 }, pt: { xs: 3, lg: 4 }, pb: 5 }}
					>
						<Card
							className="flow"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								backgroundColor: "transparent !important",
								boxShadow: "none !important",
								overflow: "hidden",
								mt: 0,
							}}
						>
							<Card
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}
							>
								<SoftBox
										sx={{
										width: "100%",
										margin: "0 auto",
										pt: 0,
									}}
								>
									<SoftBox
										sx={{
											width: "100%",
											margin: "0 auto",
											px: 0,
											pb: 1,
										}}
									>
										<GoBackButton />
									</SoftBox>

									<Header
										otherUser={otherUser}
										isOwner={isOwner}
										label="User"
									/>
								</SoftBox>

								<SoftBox sx={{ overflow: "visible" }}>
									<SoftBox
										sx={{
											width: "100%",
											margin: "0 auto",
											px: 0,
											py: 0,
										}}
									>
										<Grid container spacing={2} alignItems="flex-start">
											<Grid size={{ xs: 12, lg: 8 }}>
												<SectionCard title="Profile details" action={!isEditing && (
													<ProtectedButton permissionName={PERMISSIONS.EDIT_USERS}>
														<SoftButton variant="outlined" color="primary" size="small" onClick={() => setIsEditing(true)}>
															Edit user
														</SoftButton>
													</ProtectedButton>
												)}>
													{isEditing ? (
														<Formik initialValues={otherUser} validationSchema={validations} onSubmit={handleSubmit} enableReinitialize>
															{({ values, errors, touched, setFieldValue, dirty, resetForm }) => (
																<Form id={profileForm.formId} autoComplete="off">
																	<ProfileInfo formData={{ values, errors, touched, setFieldValue, roleOptions, formField: profileForm.formField, userRole: userRoleName }} />
																	<SoftBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
																		<SoftButton variant="outlined" color="info" onClick={() => { resetForm(); setIsEditing(false); }}>Cancel</SoftButton>
																		<SoftButton type="submit" variant="gradient" color="info" disabled={!dirty || isSaving}>Save changes</SoftButton>
																	</SoftBox>
																</Form>
															)}
														</Formik>
													) : <ProfileDetailsRows user={otherUser} roleName={userRoleName} />}
												</SectionCard>
											</Grid>

											<Grid size={{ xs: 12, lg: 4 }}>
												  <SectionCard title="Recent Activity">
													<RecentFlows recentFlows={recentFlows} embedded />
												</SectionCard>
											</Grid>
										</Grid>
									</SoftBox>
								</SoftBox>
							</Card>
						</Card>
					</SoftBox>
				</DashboardLayout>
			</>
		);
	}
}

export default Settings;
